import { DomainError } from '../errors.js';

/**
 * Yakıt ve MRV miktarları için ondalık (ADR 0117 §11): BigInt sabit nokta, kayan nokta yok
 * (`cash/money.ts` emsali; yeni bağımlılık yok). Değer `units / 10^scale`. Veritabanında ve JSON'da
 * kanonik ondalık metin saklanır; ara sonuçlar ondalıktır, yuvarlama yalnız tanımlı ölçekte (çarpma,
 * bölme) ve gösterimde yapılır. Yuvarlama yarım yukarı (sıfırdan uzağa). Saf modül.
 */

/** Girişte kabul edilen biçim: en çok 12 tam ve 12 ondalık hane; ayırıcı nokta, binlik yok. */
export const DECIMAL_PATTERN = /^-?\d{1,12}(\.\d{1,12})?$/;
/** Tam ve ondalık kısmın en çok hanesi. */
export const DECIMAL_MAX_DIGITS = 12;

/** Ondalık değer: `units / 10^scale`. */
export interface Dec {
  readonly units: bigint;
  readonly scale: number;
}

/** Fonksiyonların kabul ettiği değer: ondalık ya da ondalık metin. */
export type DecInput = Dec | string;

/** Değer kabul edilen biçimde bir ondalık metin mi. */
export function isDecimalText(value: unknown): value is string {
  return typeof value === 'string' && DECIMAL_PATTERN.test(value);
}

/** Metni ondalığa çevirir; biçim dışıysa `INVALID_DECIMAL`. */
export function parseDec(text: string): Dec {
  if (!isDecimalText(text)) throw new DomainError('INVALID_DECIMAL');
  const negative = text.startsWith('-');
  const [whole = '0', fraction = ''] = (negative ? text.slice(1) : text).split('.');
  const units = BigInt(whole + fraction);
  return { units: negative ? -units : units, scale: fraction.length };
}

function dec(value: DecInput): Dec {
  return typeof value === 'string' ? parseDec(value) : value;
}

const pow10 = (n: number) => 10n ** BigInt(n);

/** Değeri daha büyük ölçeğe taşır (kayıpsız). */
function rescaleUp(value: Dec, scale: number): bigint {
  return value.units * pow10(scale - value.scale);
}

/** `a / b`, yarım yukarı (sıfırdan uzağa) yuvarlanmış tamsayı. */
function divHalfUp(a: bigint, b: bigint): bigint {
  if (b === 0n) throw new DomainError('DIVISION_BY_ZERO');
  const negative = a < 0n !== b < 0n;
  const x = a < 0n ? -a : a;
  const y = b < 0n ? -b : b;
  const q = (2n * x + y) / (2n * y);
  return negative ? -q : q;
}

/** Ondalığı verilen ölçeğe yarım yukarı yuvarlar. */
export function decRound(value: DecInput, scale: number): Dec {
  const v = dec(value);
  if (scale >= v.scale) return { units: rescaleUp(v, scale), scale };
  return { units: divHalfUp(v.units, pow10(v.scale - scale)), scale };
}

export function decAdd(a: DecInput, b: DecInput): Dec {
  const x = dec(a);
  const y = dec(b);
  const scale = Math.max(x.scale, y.scale);
  return { units: rescaleUp(x, scale) + rescaleUp(y, scale), scale };
}

export function decNeg(a: DecInput): Dec {
  const x = dec(a);
  return { units: -x.units, scale: x.scale };
}

export function decSub(a: DecInput, b: DecInput): Dec {
  return decAdd(a, decNeg(b));
}

/** Toplam; boş liste sıfır. */
export function decSum(values: readonly DecInput[]): Dec {
  return values.reduce<Dec>((total, value) => decAdd(total, value), { units: 0n, scale: 0 });
}

/** Çarpım, `scale` ondalığa yarım yukarı yuvarlanmış. */
export function decMul(a: DecInput, b: DecInput, scale: number): Dec {
  const x = dec(a);
  const y = dec(b);
  return decRound({ units: x.units * y.units, scale: x.scale + y.scale }, scale);
}

/** Bölüm, `scale` ondalığa yarım yukarı yuvarlanmış; sıfıra bölme `DIVISION_BY_ZERO`. */
export function decDiv(a: DecInput, b: DecInput, scale: number): Dec {
  const x = dec(a);
  const y = dec(b);
  if (y.units === 0n) throw new DomainError('DIVISION_BY_ZERO');
  // x/y = (xu / 10^xs) / (yu / 10^ys); sonuç × 10^scale
  const numerator = x.units * pow10(scale + y.scale);
  const denominator = y.units * pow10(x.scale);
  return { units: divHalfUp(numerator, denominator), scale };
}

/** Karşılaştırma: -1, 0, 1 (ölçekten bağımsız). */
export function decCmp(a: DecInput, b: DecInput): -1 | 0 | 1 {
  const diff = decSub(a, b).units;
  return diff < 0n ? -1 : diff > 0n ? 1 : 0;
}

export function decIsZero(a: DecInput): boolean {
  return dec(a).units === 0n;
}

export function decIsNegative(a: DecInput): boolean {
  return dec(a).units < 0n;
}

/**
 * Metne çevirir. `fixed` verilmezse kanonik biçim: sondaki ondalık sıfırlar atılır, eksi sıfır
 * yok. `fixed` verilirse o kadar ondalığa yarım yukarı yuvarlanır ve doldurulur (yalnız gösterim).
 */
export function formatDec(value: DecInput, fixed?: number): string {
  const v = fixed === undefined ? dec(value) : decRound(value, fixed);
  const negative = v.units < 0n;
  const digits = (negative ? -v.units : v.units).toString().padStart(v.scale + 1, '0');
  const whole = digits.slice(0, digits.length - v.scale);
  let fraction = digits.slice(digits.length - v.scale);
  if (fixed === undefined) fraction = fraction.replace(/0+$/, '');
  const zero = /^0*$/.test(whole + fraction);
  const sign = negative && !zero ? '-' : '';
  return fraction === '' ? `${sign}${whole}` : `${sign}${whole}.${fraction}`;
}

/** Ondalık metni kanonik biçime getirir (`007.500` → `7.5`, `-0.0` → `0`). */
export function canonicalDec(text: string): string {
  return formatDec(parseDec(text));
}
